import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Placeholder, Row, Spinner } from 'react-bootstrap';
import DashboardPieChart from 'components/dashboards/chart/DashboardPieChart';
import SentimentAnalysisLineChart from './SentimentAnalysisLineChart';
import { MONTHS, WEEK, YEARS } from 'data/common';
import { getColor, getTotalNumber } from 'helpers/utils';
import { useParams } from 'react-router-dom';
import axiosClient from 'config/axios';
import Percentage from '../../Percentage';
import Flex from 'components/common/Flex';

const SentimentAnalysis = () => {
  const { emailAccountId } = useParams();
  const [dataWeekly, setDataWeekly] = useState(null);
  const [dataOverTime, setDataOverTime] = useState(null);
  const [selectedOption, setSelectedOption] = useState(WEEK);

  const [isFirstLoadingOverTime, setIsFirstLoadingOverTime] = useState(true);
  const [isLoadingOverTime, setIsLoadingOverTime] = useState(true);
  const [hasErrorOverTime, setHasErrorOverTime] = useState(false);
  const [isLoadingWeekly, setIsLoadingWeekly] = useState(true);
  const [hasErrorWeekly, setHasErrorWeekly] = useState(false);

  const pieChartColor = [
    getColor('primary'),
    getColor('danger'),
    getColor('gray-400')
  ];

  const handleFiltering = e => {
    const selectedValue = e.target.value;
    setSelectedOption(selectedValue);
  };

  const fetchWeeklyData = async () => {
    try {
      const params = { filterType: 'week', emailAccountId: emailAccountId };
      const response = await axiosClient.get(`/conversation/sentiment`, {
        params
      });
      setDataWeekly({
        Positive: response.data.sentiment['Positive'],
        Negative: response.data.sentiment['Negative'],
        Neutral: response.data.sentiment['Neutral']
      });
      setIsLoadingWeekly(false);
      setHasErrorWeekly(false);
    } catch (error) {
      setIsLoadingWeekly(false);
      setHasErrorWeekly(true);
      console.log(error);
    }
  };

  const fetchOverTimeData = async () => {
    try {
      const params = {
        filterType: selectedOption,
        emailAccountId: emailAccountId
      };
      const response = await axiosClient.get(`/conversation/sentiment`, {
        params
      });
      setDataOverTime({
        Positive: response.data.sentiment['Positive'],
        Negative: response.data.sentiment['Negative'],
        Neutral: response.data.sentiment['Neutral']
      });
      setIsFirstLoadingOverTime(false);
      setIsLoadingOverTime(false);
      setHasErrorOverTime(false);
    } catch (error) {
      setIsFirstLoadingOverTime(false)
      setIsLoadingOverTime(false);
      setHasErrorOverTime(true);
      console.log(error);
    }
  };

  useEffect(() => {
    setIsLoadingOverTime(true);
    fetchOverTimeData();
  }, [selectedOption, emailAccountId]);

  useEffect(() => {
    fetchWeeklyData();
  }, [emailAccountId]);

  return (
    <>
      <Col xxl={4}>
        <Card className="card h-100">
          <Card.Header className="d-md-flex justify-content-between border-bottom border-200 py-3 py-md-2">
            <h6 className="mb-2 mb-md-0 py-md-2">
              Sentiment Analysis - Weekly Overview
            </h6>
          </Card.Header>
          {isLoadingWeekly ? (
            <Placeholder as={Card.Text} animation="glow">
              <div
                className="w-100 placeholder rounded"
                style={{ height: 430 }}
              ></div>
            </Placeholder>
          ) : !hasErrorWeekly && dataWeekly ? (
            <>
              <Card.Body className="p-3 d-flex aligm-items-center">
                <DashboardPieChart
                  chartData={dataWeekly}
                  color={pieChartColor}
                />
              </Card.Body>
              <Card.Footer className="py-0">
                <h6 className="text-600 my-2 fs--2 text-center">
                  Totals & Difference: This Week - Last Week
                </h6>
                <Row>
                  <Col
                    xs={4}
                    className="border-top border-end border-200 py-3 d-flex justify-content-center"
                  >
                    <div>
                      <h6 className="text-600 mb-1 fs--2">Positive</h6>
                      <h6 className="fs-0 mb-0 d-flex align-items-center">
                        {getTotalNumber(dataWeekly['Positive']?.chartData)}
                        <Percentage
                          percentage={dataWeekly['Positive']?.percentage | 0}
                          noBackground={true}
                          className={'fs--2'}
                        />
                      </h6>
                    </div>
                  </Col>
                  <Col
                    xs={4}
                    className="border-top border-end border-200 py-3 d-flex justify-content-center"
                  >
                    <div>
                      <h6 className="text-600 mb-1 fs--2">Negative</h6>
                      <h6 className="fs-0 mb-0 d-flex align-items-center">
                        {getTotalNumber(dataWeekly['Negative']?.chartData)}
                        <Percentage
                          percentage={dataWeekly['Negative']?.percentage | 0}
                          noBackground={true}
                          className={'fs--2'}
                        />
                      </h6>
                    </div>
                  </Col>
                  <Col
                    xs={4}
                    className="border-top py-3 d-flex justify-content-center"
                  >
                    <div>
                      <h6 className="text-600 mb-1 fs--2">Neutral</h6>
                      <h6 className="fs-0 mb-0 d-flex align-items-center">
                        {getTotalNumber(dataWeekly['Neutral']?.chartData)}
                        <Percentage
                          percentage={dataWeekly['Neutral']?.percentage | 0}
                          noBackground={true}
                          className={'fs--2'}
                        />
                      </h6>
                    </div>
                  </Col>
                </Row>
              </Card.Footer>
            </>
          ) : (
            <div className="py-5 m-auto">No data</div>
          )}
        </Card>
      </Col>
      <Col xxl={8}>
        <Card className="card h-100">
          <Card.Header className="d-md-flex justify-content-between border-bottom border-200 py-3 py-md-2">
            <h6 className="mb-2 mb-md-0 py-md-2">
              Sentiment Analysis Over Time
            </h6>
            <Row className="flex-between-center g-0">
              <Col xs="auto">
                <Form.Select
                  value={selectedOption}
                  onChange={handleFiltering}
                  size="sm"
                >
                  <option value={WEEK}>Week</option>
                  <option value={MONTHS}>Months</option>
                  <option value={YEARS}>Years</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Header>
          {isFirstLoadingOverTime ? (
            <Placeholder as={Card.Text} animation="glow">
              <div
                className="w-100 placeholder rounded"
                style={{ height: 430 }}
              ></div>
            </Placeholder>
          ) : (
              <Card.Body
                className="p-3 d-flex aligm-items-center"
                style={{ height: 430 }}
              >
                {isLoadingOverTime ? (
                  <Flex
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    className="position-absolute start-50 top-50 translate-middle"
                  >
                    <Spinner animation="border" role="status"></Spinner>
                    <span className="mt-2">Loading...</span>
                  </Flex>
                ) : !hasErrorOverTime && dataOverTime ? (
                  <SentimentAnalysisLineChart
                    data={dataOverTime}
                    filter={selectedOption}
                  />
                ) : (
                  <div className="m-auto">No data</div>
                )}
              </Card.Body>
          )}
        </Card>
      </Col>
    </>
  );
};

export default SentimentAnalysis;
